import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, FileText, IndianRupee, TrendingUp, Building2, Briefcase, Calendar, Search, Plus, Edit2, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { toast } from 'sonner';
import PINGate from '../components/PINGate';

const HRDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState(false);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimeoutRef = useRef(null);
  
  // Department management
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', description: '' });

  useEffect(() => {
    if (pinVerified) {
      fetchData();
    }
  }, [pinVerified]);

  const fetchData = async () => {
    try {
      const [statsRes, deptRes] = await Promise.all([
        api.getHRFinanceStats(),
        api.getDepartments()
      ]);
      setStats(statsRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error('Failed to load HR data');
    } finally {
      setLoading(false);
    }
  };

  // Global Search with debouncing
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    
    setSearching(true);
    
    // Debounce API call by 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.hrGlobalSearch(query);
        setSearchResults(res.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  // Department CRUD
  const handleSaveDepartment = async () => {
    if (!deptForm.name.trim()) {
      toast.error('Department name is required');
      return;
    }
    
    try {
      if (editingDept) {
        await api.updateDepartment(editingDept.id, deptForm);
        toast.success('Department updated');
      } else {
        await api.createDepartment(deptForm);
        toast.success('Department created');
      }
      setShowDeptModal(false);
      setEditingDept(null);
      setDeptForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save department');
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Delete "${dept.name}" department?`)) return;
    
    try {
      await api.deleteDepartment(dept.id);
      toast.success('Department deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const openEditDept = (dept) => {
    setEditingDept(dept);
    setDeptForm({ name: dept.name, description: dept.description || '' });
    setShowDeptModal(true);
  };

  const DashboardContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header with Global Search */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">HR Dashboard</h1>
            <p className="text-slate-500">Human Resources Overview</p>
          </div>
          
          {/* Global Search */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
            <Input
              placeholder="Search by name, phone, or employee ID..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
              data-testid="hr-global-search"
            />
            
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                {searchResults.map(result => (
                  <div
                    key={result.id}
                    onClick={() => {
                      navigate(`/hr/employees?highlight=${result.id}`);
                      setSearchResults([]);
                      setSearchQuery('');
                    }}
                    className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                  >
                    <div className="font-medium text-slate-900">{result.name}</div>
                    <div className="text-sm text-slate-500 flex gap-2">
                      {result.profile?.employee_code && <span>ID: {result.profile.employee_code}</span>}
                      {result.profile?.phone_number && <span>📞 {result.profile.phone_number}</span>}
                      {result.profile?.department && <span>🏢 {result.profile.department}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searching && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg p-3 text-center text-slate-500">
                Searching...
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card 
            className="border-l-4 border-l-indigo-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/hr/employees')}
            data-testid="card-total-employees"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Employees</CardTitle>
              <Users className="h-5 w-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.total_employees || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Click to view all →</p>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/hr/documents')}
            data-testid="card-documents"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Documents</CardTitle>
              <FileText className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.total_documents || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Click to manage →</p>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/hr/salary')}
            data-testid="card-salary"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Salary Configured</CardTitle>
              <IndianRupee className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">{stats?.employees_with_salary_config || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Click to view salary →</p>
            </CardContent>
          </Card>

          <Card 
            className="border-l-4 border-l-purple-500 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate('/hr/employees?filter=pending_salary')}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Pending Setup</CardTitle>
              <TrendingUp className="h-5 w-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900">
                {(stats?.total_employees || 0) - (stats?.employees_with_salary_config || 0)}
              </div>
              <p className="text-xs text-slate-500 mt-1">Need salary configuration</p>
            </CardContent>
          </Card>
        </div>

        {/* Departments Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-slate-500" />
              Departments
            </CardTitle>
            <Button 
              size="sm" 
              onClick={() => {
                setEditingDept(null);
                setDeptForm({ name: '', description: '' });
                setShowDeptModal(true);
              }}
              data-testid="add-department-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Department
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {departments.map(dept => (
                <div 
                  key={dept.id} 
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/hr/employees?department=${encodeURIComponent(dept.name)}`)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xl font-bold text-slate-900">{dept.employee_count || 0}</div>
                      <div className="text-sm font-medium text-slate-700">{dept.name}</div>
                      {dept.description && (
                        <div className="text-xs text-slate-500 mt-1">{dept.description}</div>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={(e) => { e.stopPropagation(); openEditDept(dept); }}
                        className="p-1 hover:bg-slate-200 rounded"
                      >
                        <Edit2 className="h-4 w-4 text-slate-500" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteDepartment(dept); }}
                        className="p-1 hover:bg-red-100 rounded"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {departments.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  No departments yet. Click "Add Department" to create one.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Employees by Role */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-slate-500" />
              Employees by Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {stats?.employees_by_role && Object.entries(stats.employees_by_role).map(([role, count]) => (
                <div 
                  key={role} 
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/hr/employees?role=${role}`)}
                >
                  <div className="text-2xl font-bold text-slate-900">{count}</div>
                  <div className="text-sm text-slate-600 capitalize">{role.replace(/_/g, ' ')}</div>
                </div>
              ))}
              {(!stats?.employees_by_role || Object.keys(stats.employees_by_role).length === 0) && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  No employee data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Department Modal */}
        {showDeptModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">
                  {editingDept ? 'Edit Department' : 'Add Department'}
                </h2>
                <button onClick={() => setShowDeptModal(false)}>
                  <X className="h-5 w-5 text-slate-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Department Name *</label>
                  <Input
                    value={deptForm.name}
                    onChange={(e) => setDeptForm({...deptForm, name: e.target.value})}
                    placeholder="e.g., Sales, HR, Engineering"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Input
                    value={deptForm.description}
                    onChange={(e) => setDeptForm({...deptForm, description: e.target.value})}
                    placeholder="Optional description"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowDeptModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveDepartment}>
                  {editingDept ? 'Update' : 'Create'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PINGate 
      module="hr" 
      onVerified={() => setPinVerified(true)}
      title="HR Module"
    >
      <DashboardContent />
    </PINGate>
  );
};

export default HRDashboard;
